import React, { useEffect, useState } from "react";
import { AreaChart, Area, XAxis, YAxis, CartesianGrid, Tooltip, ResponsiveContainer, ReferenceDot } from "recharts";
import { useTranslation } from "src/_common/hooks/useTranslation";
import { useEmployerJobApi } from "src/_common/hooks/actions/job/appEmployerJobApiHook";
// import "./ApplicationChart.css";

interface ApplicationChartProps {
  data?: Array<{
    day: string;
    applications: number;
  }>;
  className?: string;
}

const ApplicationChart: React.FC<ApplicationChartProps> = ({ data: propData, className }) => {
  const { t } = useTranslation("dashboard");
  const { callGetAppliedJobsLastSevenDays } = useEmployerJobApi();
  const [data, setData] = useState<
    Array<{
      day: string;
      applications: number;
      date?: string;
      fullDayName?: string;
    }>
  >(propData || []);
  const [loading, setLoading] = useState(false);

  useEffect(() => {
    loadAppliedJobsLastSevenDays();
  }, []);

  const loadAppliedJobsLastSevenDays = () => {
    setLoading(true);
    callGetAppliedJobsLastSevenDays(
      {},
      (message: string, apiData: any) => {
        console.log("Applied jobs last seven days:", apiData);
        if (apiData && apiData.daily_counts && Array.isArray(apiData.daily_counts)) {
          // Transform API data to chart format
          const transformedData = apiData.daily_counts.map((item: any) => ({
            day: item.day_name.toLowerCase().substring(0, 3), // "Thursday" -> "thu"
            applications: item.count,
            date: item.date,
            fullDayName: item.day_name,
          }));
          setData(transformedData);
        }
        setLoading(false);
      },
      (error: string) => {
        console.error("Failed to load applied jobs data:", error);
        setLoading(false);
      }
    );
  };

  // Transform data to include localized day names
  const chartData = data.map((item) => ({
    ...item,
    dayName: item.day ? t(`dashboard.chart.days.${item.day}`) : item.fullDayName || item.day,
    fullDay: item.fullDayName || (item.day ? t(`dashboard.chart.days.${item.day}`) : item.day),
  }));

  // Custom tooltip component
  const CustomTooltip = ({ active, payload, label }: any) => {
    if (active && payload && payload.length) {
      const data = payload[0].payload;
      return (
        <div className="chart-tooltip">
          <p className="tooltip-label">{data.fullDay}</p>
          <p className="tooltip-value">{t("dashboard.chart.tooltip", { count: payload[0].value })}</p>
        </div>
      );
    }
    return null;
  };

  // Custom dot component for highlighting Thursday
  const CustomDot = (props: any) => {
    const { cx, cy, payload } = props;
    if (payload.day === "thu") {
      return (
        <g>
          <circle cx={cx} cy={cy} r="6" fill="#1f2937" stroke="#3b82f6" strokeWidth="2" />
          <circle cx={cx} cy={cy} r="3" fill="#3b82f6" />
        </g>
      );
    }
    return <circle cx={cx} cy={cy} r="4" fill="#3b82f6" />;
  };

  return (
    <div className={`application-chart ${className || ""}`}>
      <ResponsiveContainer width="100%" height={300}>
        <AreaChart
          data={chartData}
          margin={{
            top: 20,
            right: 30,
            left: 20,
            bottom: 20,
          }}
        >
          <defs>
            <linearGradient id="colorApplications" x1="0" y1="0" x2="0" y2="1">
              <stop offset="5%" stopColor="#3b82f6" stopOpacity={0.3} />
              <stop offset="95%" stopColor="#3b82f6" stopOpacity={0.05} />
            </linearGradient>
          </defs>

          <CartesianGrid strokeDasharray="3 3" stroke="#f0f0f0" horizontal={true} vertical={false} />

          <XAxis dataKey="dayName" axisLine={false} tickLine={false} tick={{ fill: "#666", fontSize: 12 }} dy={10} />

          <YAxis
            axisLine={false}
            tickLine={false}
            tick={{ fill: "#666", fontSize: 12 }}
            domain={[20, 60]}
            ticks={[20, 30, 40, 50, 60]}
          />

          <Tooltip content={<CustomTooltip />} cursor={{ stroke: "#3b82f6", strokeWidth: 1, strokeDasharray: "3 3" }} />

          <Area
            type="monotone"
            dataKey="applications"
            stroke="#3b82f6"
            strokeWidth={3}
            fillOpacity={1}
            fill="url(#colorApplications)"
            dot={<CustomDot />}
            activeDot={{
              r: 6,
              fill: "#3b82f6",
              stroke: "#1f2937",
              strokeWidth: 2,
            }}
          />
        </AreaChart>
      </ResponsiveContainer>
    </div>
  );
};

export default ApplicationChart;
